import { Injectable, inject } from '@angular/core';
import { IPermiso, IQueryParam, Nivel, TipoEntidadPermiso } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { Lista2Service } from './lista2.service';

/**
 * Alcance resuelto de un permiso: qué ve, con nombres legibles.
 *
 * - Nivel Cliente: el cliente (+ dependientes si includeChildren).
 * - Nivel Grupo: los grupos poblados (virtual `grupos`).
 * - Nivel Entidad: las entidades poblados según tipoEntidad. Activo/Vehículo/
 *   Colectivo caen todos en el virtual `activos` (son la misma colección); las
 *   Cámaras no tienen virtual en el schema del permiso y se resuelven acá con
 *   una consulta aparte cacheada.
 */
export interface IAlcancePermiso {
  nivel: Nivel;
  icono: string;
  tipoEntidad?: TipoEntidadPermiso;
  /** Etiqueta del alcance: 'Cliente', 'Grupos', 'Activos', 'Cámaras', ... */
  label: string;
  /** Nombres resueltos (grupos o entidades); vacío en nivel Cliente. */
  nombres: string[];
  /** Ids del permiso que no se pudieron resolver a un nombre. */
  sinResolver: number;
  includeChildren?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PermisoAlcanceService {
  private lista2 = inject(Lista2Service);

  // Cache de cámaras (id -> identificación): el permiso no las puebla.
  private camarasMap = new Map<string, string>();
  private camarasCargadas = false;

  /**
   * Precarga lo que el populate del permiso no trae. Llamar después de listar,
   * antes de pintar el alcance. Idempotente y silenciosa: si falla, el alcance
   * cae al conteo de ids.
   */
  public async precargar(permisos: IPermiso[] = []): Promise<void> {
    const hayCamaras = permisos.some(
      (p) => p.nivel === 'Entidad' && p.tipoEntidad === 'Cámara',
    );
    if (!hayCamaras || this.camarasCargadas) return;
    const query: IQueryParam = {
      select: 'identificacion',
      unlimited: true,
      includeChildren: true,
    };
    try {
      const res = await firstValueFrom(this.lista2.camaras(query));
      res?.datos?.forEach((c) =>
        this.camarasMap.set(c._id!, c.identificacion || c._id!),
      );
      this.camarasCargadas = true;
    } catch (e) {
      console.error('Error cargando cámaras para alcance de permisos', e);
    }
  }

  public icono(nivel?: Nivel): string {
    switch (nivel) {
      case 'Grupo':
        return 'folder_shared';
      case 'Entidad':
        return 'devices';
      default:
        return 'business';
    }
  }

  public alcance(permiso: IPermiso): IAlcancePermiso {
    const nivel: Nivel = permiso.nivel || 'Cliente';
    const base: IAlcancePermiso = {
      nivel,
      icono: this.icono(nivel),
      label: 'Cliente',
      nombres: [],
      sinResolver: 0,
      includeChildren: permiso.includeChildren,
    };

    if (nivel === 'Grupo') {
      const ids = permiso.idsGrupos || [];
      const mapa = new Map(
        (permiso.grupos || []).map((g) => [
          g._id,
          g.categoria ? `${g.nombre} (${g.categoria})` : (g.nombre ?? g._id),
        ]),
      );
      return {
        ...base,
        label: 'Grupos',
        ...this.resolver(ids, mapa),
      };
    }

    if (nivel === 'Entidad') {
      const tipoEntidad = permiso.tipoEntidad;
      return {
        ...base,
        tipoEntidad,
        label: tipoEntidad ? `${tipoEntidad}s` : 'Entidades',
        ...this.resolver(
          permiso.idsEntidades || [],
          this.mapaEntidades(permiso),
        ),
      };
    }

    return base;
  }

  /** Texto corto para la celda de la tabla: 'Activos: A, B (+3)'. */
  public resumen(alcance: IAlcancePermiso, maximo = 2): string {
    if (alcance.nivel === 'Cliente') {
      return alcance.includeChildren ? 'Incluye dependientes' : '';
    }
    const total = alcance.nombres.length + alcance.sinResolver;
    if (!total) return 'Sin asignar';
    const visibles = alcance.nombres.slice(0, maximo).join(', ');
    const resto = total - Math.min(alcance.nombres.length, maximo);
    return resto > 0 ? `${visibles} (+${resto})` : visibles;
  }

  private mapaEntidades(permiso: IPermiso): Map<string, string> {
    const mapa = new Map<string, string>();
    switch (permiso.tipoEntidad) {
      // Vehículo y Colectivo son Activos: el mismo virtual los puebla.
      case 'Activo':
      case 'Vehículo':
      case 'Colectivo':
        permiso.activos?.forEach((a) =>
          mapa.set(a._id!, a.identificacion || a._id!),
        );
        break;
      case 'Luminaria':
        permiso.luminarias?.forEach((l) =>
          mapa.set(l._id!, l.identificacion || l._id!),
        );
        break;
      case 'Alarma':
        permiso.alarmas?.forEach((a) => mapa.set(a._id!, a.nombre || a._id!));
        break;
      case 'Cámara':
        return this.camarasMap;
    }
    return mapa;
  }

  private resolver(
    ids: string[],
    mapa: Map<string, string | undefined>,
  ): { nombres: string[]; sinResolver: number } {
    const nombres: string[] = [];
    let sinResolver = 0;
    ids.forEach((id) => {
      const nombre = mapa.get(id);
      if (nombre) nombres.push(nombre);
      else sinResolver++;
    });
    return { nombres, sinResolver };
  }
}
